import { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export interface OrderOption {
  label: string;
  value: string;
  field: string;
  direction: 'asc' | 'desc';
}

export const orderOptions: OrderOption[] = [
  {
    label: 'Requerente (A-Z)', value: 'requerente_asc', field: 'requerente', direction: 'asc',
  },
  {
    label: 'Requerente (Z-A)', value: 'requerente_desc', field: 'requerente', direction: 'desc',
  },
  {
    label: 'Espécie (A-Z)', value: 'especie_asc', field: 'especie', direction: 'asc',
  },
  {
    label: 'Espécie (Z-A)', value: 'especie_desc', field: 'especie', direction: 'desc',
  },
  {
    label: 'Data Entrada (Mais Recente)', value: 'data_entrada_desc', field: 'data_entrada', direction: 'desc',
  },
  {
    label: 'Data Entrada (Mais Antiga)', value: 'data_entrada_asc', field: 'data_entrada', direction: 'asc',
  },
  {
    label: 'Número Amostra (Crescente)', value: 'numero_amostra_asc', field: 'numero_amostra', direction: 'asc',
  },
  {
    label: 'Número Amostra (Decrescente)', value: 'numero_amostra_desc', field: 'numero_amostra', direction: 'desc',
  },
];

export interface TetrazolioOrderHookData {
  selectedOrder: OrderOption;
  changeOrder: (option: OrderOption | string) => void;
}

export default function useTetrazolioOrder(urlPrefix = 'tz'): TetrazolioOrderHookData {
  const history = useHistory();
  const location = useLocation();

  const getInitialOrder = useCallback((): OrderOption => {
    const searchParams = new URLSearchParams(location.search);
    const orderBy = searchParams.get(`${urlPrefix}_orderBy`) || 'data_entrada';
    const orderDirection = (searchParams.get(`${urlPrefix}_orderDirection`) as 'asc' | 'desc') || 'desc';

    const foundOption = orderOptions.find(
      (option) => option.field === orderBy && option.direction === orderDirection,
    );

    return foundOption || orderOptions[4];
  }, [location.search, urlPrefix]);

  const [selectedOrder, setSelectedOrder] = useState<OrderOption>(getInitialOrder());

  const updateURL = useCallback((orderBy: string, orderDirection: 'asc' | 'desc') => {
    const searchParams = new URLSearchParams(location.search);

    searchParams.set(`${urlPrefix}_orderBy`, orderBy);
    searchParams.set(`${urlPrefix}_orderDirection`, orderDirection);
    searchParams.set('page', '1');

    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }, [history, location.pathname, location.search, urlPrefix]);

  const changeOrder = useCallback((option: OrderOption | string) => {
    let selectedOption: OrderOption;

    if (typeof option === 'string') {
      const foundOption = orderOptions.find((opt) => opt.value === option);
      selectedOption = foundOption || orderOptions[4];
    } else {
      selectedOption = option;
    }

    setSelectedOrder(selectedOption);
    updateURL(selectedOption.field, selectedOption.direction);
  }, [updateURL]);

  useEffect(() => {
    setSelectedOrder(getInitialOrder());
  }, [getInitialOrder]);

  return {
    selectedOrder,
    changeOrder,
  };
}
